import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SymbolSearchInput from "@/components/SymbolSearchInput";
import BotConfidenceBadge from "@/components/BotConfidenceBadge";
import BotConfidenceHistory from "@/components/BotConfidenceHistory";
import ConfidenceBandsLegend from "@/components/ConfidenceBandsLegend";
import BotPauseTimeline from "@/components/BotPauseTimeline";
import ArchivedBotsCard from "@/components/ArchivedBotsCard";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, Play, Robot, ChartLineUp, EnvelopeSimple, Check, Sparkle, PlugsConnected, Scales, PauseCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import BacktestModal from "@/components/BacktestModal";
import BotCompareModal from "@/components/BotCompareModal";
import { fireConfetti } from "@/lib/confetti";
import { playChime } from "@/lib/chime";

const STRATEGIES = [
  { value: "ma_crossover", label: "Moving Average Crossover", desc: "Buys when fast SMA crosses above slow SMA, sells on cross-below." },
  { value: "rsi", label: "RSI Mean-Reversion", desc: "Buys when RSI drops below oversold, sells when above overbought." },
  { value: "macd", label: "MACD Signal", desc: "Buys when MACD line crosses above its signal line, sells on cross-below." },
  { value: "bollinger", label: "Bollinger Bands", desc: "Buys when price touches lower band (mean reversion), sells at upper band." },
];

export default function BotsPage() {
  const [bots, setBots] = useState([]);
  const [logs, setLogs] = useState([]);
  const [open, setOpen] = useState(false);
  const [backtestOpen, setBacktestOpen] = useState(false);
  const [backtestConfig, setBacktestConfig] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareIds, setCompareIds] = useState([]); // selected bot ids
  const [trendFilter, setTrendFilter] = useState("all"); // all | rising | falling | flat
  // Iter 47 — persist sort direction so power users don't have to re-flip
  // the arrow on every session. Default true (worst/biggest first) matches
  // the historical behaviour for users with no cached preference.
  // Iter 48 — backend `trend_sort_desc` on user_settings is now source of
  // truth; localStorage acts as an offline/first-paint cache so hydration
  // is instant. `hydratedTrendSort` guards initial-load PUTs.
  const [trendSortDesc, setTrendSortDesc] = useState(() => {
    try {
      const raw = localStorage.getItem("stratos.bots.trendSortDesc");
      if (raw === "true") return true;
      if (raw === "false") return false;
      return true;
    } catch { return true; }
  });
  const [hydratedTrendSort, setHydratedTrendSort] = useState(false);
  useEffect(() => {
    let cancelled = false;
    api.get("/settings/notifications")
      .then((r) => {
        if (cancelled) return;
        const v = r.data?.trend_sort_desc;
        if (typeof v === "boolean") {
          setTrendSortDesc(v);
          try { localStorage.setItem("stratos.bots.trendSortDesc", String(v)); } catch {}
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setHydratedTrendSort(true); });
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    try { localStorage.setItem("stratos.bots.trendSortDesc", String(trendSortDesc)); } catch {}
    // Only PUT after initial hydration to avoid the localStorage-default
    // clobbering the server value during the first render window.
    if (!hydratedTrendSort) return;
    api.put("/settings/notifications", { trend_sort_desc: trendSortDesc }).catch(() => {});
  }, [trendSortDesc, hydratedTrendSort]);

  // Iter 48 — one-time migration toast so returning users discover that
  // their restore-confirm threshold now syncs across browsers. Fires once
  // per browser (localStorage flag) and is intentionally low-key so it
  // doesn't feel like a marketing pop-up.
  useEffect(() => {
    try {
      const KEY = "stratos.migration.thresholdSync.seen";
      if (localStorage.getItem(KEY) === "1") return;
      // Defer a tick so it never fights the initial page load.
      const t = setTimeout(() => {
        toast.success("Your restore-confirm preference now syncs across browsers", {
          description: "Tune it once — every device follows",
          duration: 6000,
          id: "threshold-sync-migration",
        });
        try { localStorage.setItem(KEY, "1"); } catch {}
      }, 1200);
      return () => clearTimeout(t);
    } catch { /* localStorage unavailable — silently skip */ }
  }, []);
  const [copyMode, setCopyMode] = useState(false); // dialog opened via ?copy=1
  const [form, setForm] = useState({
    symbol: "",
    strategy: "ma_crossover",
    quantity: 1,
    notify_email: false,
    fast_period: 20,
    slow_period: 50,
    rsi_period: 14,
    rsi_oversold: 30,
    rsi_overbought: 70,
    macd_fast: 12,
    macd_slow: 26,
    macd_signal: 9,
    bb_period: 20,
    bb_std: 2.0,
  });

  const load = async () => {
    try {
      const [b, l] = await Promise.all([api.get("/bots"), api.get("/bots/logs?limit=30")]);
      setBots(b.data.bots || []);
      setLogs(l.data.logs || []);
    } catch {}
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  // Support ?copy=1&symbol=…&strategy=…&fast_period=…  from public leaderboard
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("copy") !== "1") return;
    setForm((prev) => {
      const next = { ...prev };
      const s = searchParams.get("symbol");
      const strat = searchParams.get("strategy");
      const fromBot = searchParams.get("from_bot");
      if (s) next.symbol = s.toUpperCase();
      if (strat) next.strategy = strat;
      if (fromBot) next.copied_from = fromBot;
      // pass-through numeric params
      ["fast_period", "slow_period", "rsi_period", "rsi_oversold", "rsi_overbought",
       "macd_fast", "macd_slow", "macd_signal", "bb_period", "bb_std"].forEach((k) => {
        const v = searchParams.get(k);
        if (v !== null && v !== "") next[k] = Number(v);
      });
      return next;
    });
    setOpen(true);
    setCopyMode(true);
    // clear the query params so a refresh doesn't re-open the dialog
    const cleared = new URLSearchParams(searchParams);
    ["copy", "symbol", "strategy", "from_bot", "fast_period", "slow_period", "rsi_period",
     "rsi_oversold", "rsi_overbought", "macd_fast", "macd_slow", "macd_signal",
     "bb_period", "bb_std"].forEach((k) => cleared.delete(k));
    setSearchParams(cleared, { replace: true });
    // Re-run when the URL query changes so same-route SPA nav (e.g. a Copy
    // link clicked from /bots itself) still opens the dialog.
  }, [searchParams]);

  const create = async () => {
    try {
      const { data } = await api.post("/bots", { ...form, symbol: form.symbol.toUpperCase() });
      if (data?.is_first_bot) {
        // First-bot celebration: confetti + chime, so a fresh user's first
        // deploy feels like the milestone it is.
        fireConfetti();
        playChime();
        toast.success("🎉 First bot deployed! Welcome to STRATOS.", {
          description: "Your bot is live and evaluating every 2 minutes.",
        });
      } else {
        toast.success("Bot created & activated");
      }
      setOpen(false);
      setCopyMode(false);
      // Reset symbol AND copied_from so the next manually-created bot isn't
      // accidentally attributed to a public strategy.
      setForm({ ...form, symbol: "", copied_from: undefined });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const toggle = async (id, enabled) => {
    await api.patch(`/bots/${id}`, { enabled });
    load();
  };
  const toggleNotify = async (id, notify_email) => {
    await api.patch(`/bots/${id}/notify`, { notify_email });
    toast.success(notify_email ? "Email alerts enabled" : "Email alerts disabled");
    load();
  };
  const toggleAlpaca = async (id, use_alpaca) => {
    try {
      await api.patch(`/bots/${id}/alpaca`, { use_alpaca });
      toast.success(use_alpaca ? "Live-mirror to Alpaca ON" : "Live-mirror OFF");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };
  const toggleScaling = async (id, confidence_scaling) => {
    try {
      await api.patch(`/bots/${id}/scaling`, { confidence_scaling });
      toast.success(confidence_scaling ? "Confidence sizing ON — bot scales trades by band" : "Confidence sizing OFF");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };
  const toggleAutoPause = async (id, auto_pause_low_confidence) => {
    try {
      await api.patch(`/bots/${id}/auto-pause`, { auto_pause_low_confidence });
      toast.success(auto_pause_low_confidence
        ? "Auto-pause ON — bot pauses 24h on LOW confidence"
        : "Auto-pause OFF");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toggle failed");
    }
  };
  const resumeBot = async (id, symbol) => {
    try {
      await api.post(`/bots/${id}/resume`);
      toast.success(`${symbol} bot resumed`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resume failed");
    }
  };
  const resumeAll = async () => {
    try {
      const { data } = await api.post("/bots/resume-all");
      if (data.resumed > 0) toast.success(`Resumed ${data.resumed} bot${data.resumed === 1 ? "" : "s"}`);
      else toast.info("No paused bots to resume");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resume-all failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete bot?")) return;
    await api.delete(`/bots/${id}`);
    toast.success("Bot deleted");
    load();
  };
  const runNow = async (id) => {
    const { data } = await api.post(`/bots/${id}/run`);
    if (data.executed) toast.success(`Executed ${data.trade.side.toUpperCase()} ${data.trade.quantity} @ $${data.trade.price}`);
    else toast.info("No signal fired");
    load();
  };
  const openBacktest = (cfg) => {
    setBacktestConfig(cfg);
    setBacktestOpen(true);
  };

  // Iter 36 — after the backtest modal closes for an existing bot, force a
  // fresh confidence recompute + reload the bot list so the legend marker
  // (and the badge on the card) reflects the new score without a page refresh.
  const handleBacktestClose = async (open) => {
    setBacktestOpen(open);
    if (open) return;
    const botId = backtestConfig?.id;
    if (botId) {
      try {
        await api.get(`/bots/${botId}/confidence?force=true`);
      } catch {
        // Recompute failures are non-fatal — the marker just stays at its
        // previous position until the 24h scheduler tick refreshes it.
      }
      load();
    }
  };

  const toggleCompare = (id) => {
    setCompareIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) {
        toast.info("Compare up to 5 bots at once");
        return prev;
      }
      return [...prev, id];
    });
  };

  const openCompare = () => {
    if (compareIds.length < 2) {
      toast.info("Pick at least 2 bots to compare");
      return;
    }
    setCompareOpen(true);
  };

  // "Vary params" for Copy-With-Twist: jitter numeric strategy params by ±25%
  const varyParams = () => {
    const jitter = (n, min = 1, integer = true) => {
      const factor = 1 + (Math.random() * 0.5 - 0.25); // [-25%, +25%]
      const raw = Math.max(min, n * factor);
      return integer ? Math.round(raw) : Math.round(raw * 100) / 100;
    };
    setForm((prev) => {
      const next = { ...prev };
      if (prev.strategy === "ma_crossover") {
        next.fast_period = jitter(prev.fast_period, 2);
        next.slow_period = Math.max(next.fast_period + 1, jitter(prev.slow_period, 3));
      } else if (prev.strategy === "rsi") {
        next.rsi_period = jitter(prev.rsi_period, 2);
        next.rsi_oversold = jitter(prev.rsi_oversold, 5);
        next.rsi_overbought = Math.max(next.rsi_oversold + 5, jitter(prev.rsi_overbought, 55));
      } else if (prev.strategy === "macd") {
        next.macd_fast = jitter(prev.macd_fast, 2);
        next.macd_slow = Math.max(next.macd_fast + 1, jitter(prev.macd_slow, 5));
        next.macd_signal = jitter(prev.macd_signal, 2);
      } else if (prev.strategy === "bollinger") {
        next.bb_period = jitter(prev.bb_period, 5);
        next.bb_std = jitter(prev.bb_std, 0.5, false);
      }
      return next;
    });
    toast.success("Parameters varied ±25% — see what changes");
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="bots-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Terminal / Automation</div>
          <h1 className="heading text-4xl font-black tracking-tighter mt-2">Auto trading bots</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">
            Bots evaluate their strategy every 2 minutes on 6-months of daily bars. Signals trigger paper trades automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
        {bots.some(b => b.paused_reason === "low_confidence") && (
          <Button
            onClick={resumeAll}
            data-testid="bots-resume-all-btn"
            variant="outline"
            className="bg-transparent border-amber-500/40 text-amber-400 hover:bg-amber-500/10 rounded-sm h-10 mono text-xs uppercase tracking-widest gap-1.5"
          >
            <Play size={14} weight="bold" /> Resume all paused
          </Button>
        )}
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCopyMode(false); }}>
          <DialogTrigger asChild>
            <Button data-testid="new-bot-btn" className="bg-[#00C805] hover:bg-[#00b004] text-black rounded-sm h-10 mono text-xs uppercase tracking-widest gap-1.5">
              <Plus size={14} weight="bold" /> New bot
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 rounded-sm max-w-md">
            <DialogHeader>
              <DialogTitle className="heading text-2xl tracking-tighter">
                {copyMode ? "Copy strategy" : "Configure bot"}
              </DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Bots evaluate every 2 minutes on daily bars and paper-trade on signal.
              </DialogDescription>
            </DialogHeader>
            {copyMode && (
              <div
                data-testid="copy-twist-banner"
                className="border border-[#00C805]/40 bg-[#00C805]/5 rounded-sm px-3 py-2.5 flex items-start gap-3"
              >
                <Sparkle size={18} weight="fill" className="text-[#00C805] mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="text-xs text-white font-semibold">You're copying a public strategy</div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    Blindly cloning is a coin-flip. Tweak the parameters below — even a small twist
                    can change everything.
                  </div>
                </div>
                <Button
                  type="button"
                  data-testid="vary-params-btn"
                  onClick={varyParams}
                  variant="outline"
                  className="border-[#00C805]/50 hover:border-[#00C805] hover:bg-[#00C805]/10 text-[#00C805] mono text-[10px] uppercase tracking-widest rounded-sm h-8 px-2.5 shrink-0"
                >
                  Vary ±25%
                </Button>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Symbol</Label>
                <div className="mt-1.5">
                  <SymbolSearchInput
                    value={form.symbol}
                    onChange={(v) => setForm({ ...form, symbol: v })}
                    placeholder="Search AAPL, MSFT…"
                    testId="bot-symbol-input"
                  />
                </div>
              </div>
              <div>
                <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Strategy</Label>
                <Select value={form.strategy} onValueChange={(v) => setForm({ ...form, strategy: v })}>
                  <SelectTrigger data-testid="bot-strategy-select" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 rounded-sm">
                    {STRATEGIES.map(s => <SelectItem key={s.value} value={s.value} className="mono text-sm">{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-2">{STRATEGIES.find(s => s.value === form.strategy)?.desc}</p>
              </div>
              <div>
                <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Quantity per signal</Label>
                <Input type="number" min="1" step="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} data-testid="bot-quantity-input" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
              </div>
              <div className="flex items-center justify-between border border-zinc-800 rounded-sm px-3 py-2.5">
                <div>
                  <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Email alerts</div>
                  <div className="text-xs text-zinc-400 mt-0.5">Notify me when this bot fires a trade</div>
                </div>
                <Switch
                  checked={form.notify_email}
                  onCheckedChange={(v) => setForm({ ...form, notify_email: v })}
                  data-testid="bot-notify-toggle-form"
                  className="data-[state=checked]:bg-[#00C805]"
                />
              </div>
              {form.strategy === "ma_crossover" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Fast SMA</Label>
                    <Input type="number" min="2" value={form.fast_period} onChange={(e) => setForm({ ...form, fast_period: Number(e.target.value) })} className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Slow SMA</Label>
                    <Input type="number" min="3" value={form.slow_period} onChange={(e) => setForm({ ...form, slow_period: Number(e.target.value) })} className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                </div>
              )}
              {form.strategy === "rsi" && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Period</Label>
                    <Input type="number" min="2" value={form.rsi_period} onChange={(e) => setForm({ ...form, rsi_period: Number(e.target.value) })} className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Oversold</Label>
                    <Input type="number" value={form.rsi_oversold} onChange={(e) => setForm({ ...form, rsi_oversold: Number(e.target.value) })} className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Overbought</Label>
                    <Input type="number" value={form.rsi_overbought} onChange={(e) => setForm({ ...form, rsi_overbought: Number(e.target.value) })} className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                </div>
              )}
              {form.strategy === "macd" && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Fast EMA</Label>
                    <Input type="number" min="2" value={form.macd_fast} onChange={(e) => setForm({ ...form, macd_fast: Number(e.target.value) })} data-testid="bot-macd-fast" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Slow EMA</Label>
                    <Input type="number" min="3" value={form.macd_slow} onChange={(e) => setForm({ ...form, macd_slow: Number(e.target.value) })} data-testid="bot-macd-slow" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Signal</Label>
                    <Input type="number" min="2" value={form.macd_signal} onChange={(e) => setForm({ ...form, macd_signal: Number(e.target.value) })} data-testid="bot-macd-signal" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                </div>
              )}
              {form.strategy === "bollinger" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Period</Label>
                    <Input type="number" min="5" value={form.bb_period} onChange={(e) => setForm({ ...form, bb_period: Number(e.target.value) })} data-testid="bot-bb-period" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                  <div>
                    <Label className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Std deviations</Label>
                    <Input type="number" step="0.1" min="0.5" value={form.bb_std} onChange={(e) => setForm({ ...form, bb_std: Number(e.target.value) })} data-testid="bot-bb-std" className="bg-zinc-950 border-zinc-700 rounded-sm mono mt-1.5" />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setOpen(false); setCopyMode(false); }} className="border-zinc-700 rounded-sm bg-transparent">Cancel</Button>
              <Button
                variant="outline"
                data-testid="bt-preview-btn"
                disabled={!form.symbol}
                onClick={() => openBacktest({ ...form, symbol: form.symbol.toUpperCase() })}
                className="border-zinc-700 rounded-sm bg-transparent hover:bg-zinc-800 gap-1.5"
              >
                <ChartLineUp size={14} /> Backtest
              </Button>
              <Button onClick={create} data-testid="bot-create-btn" className="bg-[#00C805] text-black hover:bg-[#00b004] rounded-sm">Deploy bot</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <ConfidenceBandsLegend bots={bots} onRefresh={load} />
          <ArchivedBotsCard onChange={load} />
          {(() => {
            // Compute trend per bot once so the filter bar and the card
            // list use the same classification. Trend = last-first delta
            // over the confidence_history array.
            const deltaOf = (b) => {
              const h = Array.isArray(b?.confidence_history) ? b.confidence_history : [];
              if (h.length < 2) return 0;
              const d = Number(h[h.length - 1]?.score) - Number(h[0]?.score);
              return Number.isFinite(d) ? d : 0;
            };
            const trendOf = (b) => {
              const d = deltaOf(b);
              if (d === 0) return "flat";
              return d > 0 ? "rising" : "falling";
            };
            const counts = { rising: 0, falling: 0, flat: 0 };
            const withTrend = bots.map((b) => {
              const trend = trendOf(b);
              const delta = deltaOf(b);
              counts[trend] += 1;
              return { bot: b, trend, delta };
            });
            let visible = trendFilter === "all"
              ? withTrend
              : withTrend.filter((w) => w.trend === trendFilter);
            // When filtering to a single trend direction, surface the most-
            // extreme movers first — "worst-falling at the top" for the
            // Falling tab, "biggest jumps first" for Rising. Flat/All stay
            // in their natural insertion order to avoid reshuffling users'
            // mental map of the list.
            if (trendFilter === "falling" || trendFilter === "rising") {
              visible = [...visible].sort((a, b) => {
                const diff = Math.abs(b.delta) - Math.abs(a.delta);
                return trendSortDesc ? diff : -diff;
              });
            }

            return (
              <>
                <div
                  data-testid="trend-filter-bar"
                  className="flex items-center justify-between px-4 py-2 border border-zinc-800 bg-zinc-900/40 rounded-sm"
                >
                  <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                    <span>Trend filter</span>
                    {(trendFilter === "falling" || trendFilter === "rising") && (
                      <button
                        type="button"
                        data-testid="trend-sort-direction-toggle"
                        onClick={() => setTrendSortDesc((v) => !v)}
                        title={trendSortDesc
                          ? "Currently: biggest movers first · click for smallest first"
                          : "Currently: smallest movers first · click for biggest first"}
                        className="mono text-[10px] normal-case tracking-normal text-amber-400/80 hover:text-amber-300 border border-amber-500/30 hover:border-amber-500/70 rounded-sm px-1.5 py-0.5 transition-colors flex items-center gap-1"
                      >
                        <span data-testid="trend-sort-arrow">{trendSortDesc ? "▼" : "▲"}</span>
                        sorted by |Δ score| {trendSortDesc ? "desc" : "asc"}
                      </button>
                    )}
                  </div>
                  <div className="flex items-center border border-zinc-800 rounded-sm overflow-hidden">
                    {[
                      { k: "all",     label: `All (${bots.length})`,      color: "text-white" },
                      { k: "falling", label: `Falling (${counts.falling})`, color: "text-red-400" },
                      { k: "rising",  label: `Rising (${counts.rising})`,   color: "text-[#00C805]" },
                      { k: "flat",    label: `Flat (${counts.flat})`,       color: "text-zinc-400" },
                    ].map((opt) => (
                      <button
                        key={opt.k}
                        type="button"
                        data-testid={`trend-filter-${opt.k}`}
                        onClick={() => setTrendFilter(opt.k)}
                        aria-selected={trendFilter === opt.k}
                        className={`mono text-[9px] uppercase tracking-widest px-2 py-1 transition-colors ${
                          trendFilter === opt.k
                            ? "bg-zinc-100 text-black"
                            : `${opt.color} hover:bg-zinc-800`
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                {trendFilter !== "all" && visible.length === 0 && (
                  <div
                    data-testid="trend-filter-empty"
                    className="mono text-[11px] text-zinc-500 text-center py-6 border border-zinc-800 rounded-sm bg-zinc-900/20"
                  >
                    No {trendFilter} bots. Try a different filter.
                  </div>
                )}
                {bots.length > 1 && (
            <div className="flex items-center justify-between px-4 py-2 border border-zinc-800 bg-zinc-900/50 rounded-sm">
              <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">
                {compareIds.length === 0
                  ? "Tick 2-5 bots to compare their equity curves"
                  : `${compareIds.length} bot${compareIds.length > 1 ? "s" : ""} selected`}
              </div>
              <div className="flex items-center gap-2">
                {compareIds.length > 0 && (
                  <button
                    onClick={() => setCompareIds([])}
                    data-testid="compare-clear-btn"
                    className="mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white px-2 py-1"
                  >
                    Clear
                  </button>
                )}
                <Button
                  data-testid="compare-open-btn"
                  onClick={openCompare}
                  disabled={compareIds.length < 2}
                  className="bg-[#00C805] hover:bg-[#00b004] text-black disabled:bg-zinc-800 disabled:text-zinc-500 rounded-sm h-8 mono text-[10px] uppercase tracking-widest gap-1.5"
                >
                  <ChartLineUp size={12} weight="bold" /> Compare
                </Button>
              </div>
            </div>
          )}
          {bots.length === 0 && (
            <Card className="p-12 text-center">
              <Robot size={40} className="mx-auto text-zinc-700" />
              <div className="mt-4 heading text-xl">No bots deployed</div>
              <div className="text-sm text-zinc-500 mt-2">Create your first automation to start trading hands-free.</div>
            </Card>
          )}
          {visible.map(({ bot }) => (
            <Card key={bot.id} className="p-5" data-testid={`bot-card-${bot.symbol}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggleCompare(bot.id)}
                    data-testid={`bot-compare-check-${bot.symbol}`}
                    title="Add to comparison"
                    className={`mt-0.5 w-4 h-4 rounded-sm border flex items-center justify-center transition-colors ${
                      compareIds.includes(bot.id)
                        ? "bg-[#00C805] border-[#00C805]"
                        : "border-zinc-700 hover:border-zinc-500"
                    }`}
                  >
                    {compareIds.includes(bot.id) && <Check size={10} weight="bold" className="text-black" />}
                  </button>
                  <div className="relative w-3 h-3 mt-1">
                    {bot.enabled && <div className="absolute inset-0 bg-[#00C805] rounded-full pulse-dot"></div>}
                    {!bot.enabled && <div className="absolute inset-0 bg-zinc-700 rounded-full"></div>}
                  </div>
                  <div>
                    <div className="flex items-baseline gap-3">
                      <span className="heading text-2xl font-bold mono">{bot.symbol}</span>
                      <span className="mono text-[10px] uppercase tracking-widest text-zinc-500">
                        {STRATEGIES.find(s => s.value === bot.strategy)?.label}
                      </span>
                      {bot.use_alpaca && bot.enabled && (
                        <span
                          data-testid={`bot-live-chip-${bot.symbol}`}
                          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-[#00C805] text-black font-bold flex items-center gap-1"
                          title="Trades are routed to Alpaca live"
                        >
                          <span className="w-1 h-1 bg-black rounded-full animate-pulse" />
                          LIVE
                        </span>
                      )}
                      {bot.use_alpaca && !bot.enabled && (
                        <span
                          data-testid={`bot-live-chip-${bot.symbol}`}
                          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-[#00C805]/40 text-[#00C805]/60"
                          title="Alpaca route configured but bot is paused"
                        >
                          LIVE · paused
                        </span>
                      )}
                      {bot.confidence_scaling && (
                        <span
                          data-testid={`bot-scaled-chip-${bot.symbol}`}
                          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-400 flex items-center gap-1"
                          title="Position size scales with confidence band"
                        >
                          <Scales size={9} weight="bold" /> scaled
                        </span>
                      )}
                      {bot.paused_reason === "low_confidence" && bot.paused_until && (
                        <button
                          onClick={() => resumeBot(bot.id, bot.symbol)}
                          data-testid={`bot-paused-chip-${bot.symbol}`}
                          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1 cursor-pointer"
                          title={`Resumes ${new Date(bot.paused_until).toLocaleString()} — click to resume now`}
                        >
                          <PauseCircle size={9} weight="bold" /> paused · resume now
                        </button>
                      )}
                      <BotConfidenceBadge botId={bot.id} />
                    </div>
                    <div className="mono text-xs text-zinc-500 mt-2 space-x-3">
                      <span>QTY {bot.quantity}</span>
                      {bot.strategy === "ma_crossover" && <span>· SMA {bot.fast_period}/{bot.slow_period}</span>}
                      {bot.strategy === "rsi" && <span>· RSI {bot.rsi_period} · {bot.rsi_oversold}/{bot.rsi_overbought}</span>}
                      {bot.strategy === "macd" && <span>· MACD {bot.macd_fast}/{bot.macd_slow}/{bot.macd_signal}</span>}
                      {bot.strategy === "bollinger" && <span>· BB {bot.bb_period} · {bot.bb_std}σ</span>}
                      {bot.last_signal && <span>· LAST: <span className={bot.last_signal === "buy" ? "profit" : "loss"}>{bot.last_signal.toUpperCase()}</span></span>}
                    </div>
                    <BotConfidenceHistory botId={bot.id} />
                    <BotPauseTimeline symbol={bot.symbol} events={bot.pause_events} />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleNotify(bot.id, !bot.notify_email)}
                    data-testid={`bot-notify-${bot.symbol}`}
                    title={bot.notify_email ? "Email alerts on" : "Email alerts off"}
                    className={`p-2 rounded-sm ${bot.notify_email ? "text-[#00C805] bg-[#00C805]/10" : "text-zinc-500 hover:text-white hover:bg-zinc-800"}`}
                  >
                    <EnvelopeSimple size={14} weight={bot.notify_email ? "fill" : "regular"} />
                  </button>
                  <button
                    onClick={() => toggleAlpaca(bot.id, !bot.use_alpaca)}
                    data-testid={`bot-alpaca-${bot.symbol}`}
                    title={bot.use_alpaca ? "Live-mirror to Alpaca ON — click to disable" : "Route to Alpaca (real broker)"}
                    className={`p-2 rounded-sm ${bot.use_alpaca ? "text-[#00C805] bg-[#00C805]/10" : "text-zinc-500 hover:text-white hover:bg-zinc-800"}`}
                  >
                    <PlugsConnected size={14} weight={bot.use_alpaca ? "fill" : "regular"} />
                  </button>
                  <button
                    onClick={() => toggleScaling(bot.id, !bot.confidence_scaling)}
                    data-testid={`bot-scaling-${bot.symbol}`}
                    title={bot.confidence_scaling ? "Confidence sizing ON — high=1.5x, low=0.5x. Click to disable" : "Scale trade size by confidence band"}
                    className={`p-2 rounded-sm ${bot.confidence_scaling ? "text-[#00C805] bg-[#00C805]/10" : "text-zinc-500 hover:text-white hover:bg-zinc-800"}`}
                  >
                    <Scales size={14} weight={bot.confidence_scaling ? "fill" : "regular"} />
                  </button>
                  <button
                    onClick={() => toggleAutoPause(bot.id, !bot.auto_pause_low_confidence)}
                    data-testid={`bot-autopause-${bot.symbol}`}
                    title={bot.auto_pause_low_confidence
                      ? "Auto-pause ON — will halt 24h if confidence drops to LOW. Click to disable"
                      : "Auto-pause bot for 24h if confidence drops to LOW"}
                    className={`p-2 rounded-sm ${bot.auto_pause_low_confidence ? "text-amber-400 bg-amber-500/10" : "text-zinc-500 hover:text-white hover:bg-zinc-800"}`}
                  >
                    <PauseCircle size={14} weight={bot.auto_pause_low_confidence ? "fill" : "regular"} />
                  </button>
                  <button onClick={() => openBacktest(bot)} data-testid={`bot-backtest-${bot.symbol}`} title="Backtest" className="p-2 hover:bg-zinc-800 rounded-sm text-zinc-400 hover:text-white">
                    <ChartLineUp size={14} />
                  </button>
                  <button onClick={() => runNow(bot.id)} data-testid={`bot-run-${bot.symbol}`} title="Run now" className="p-2 hover:bg-zinc-800 rounded-sm text-zinc-400 hover:text-white">
                    <Play size={14} weight="fill" />
                  </button>
                  <button onClick={() => remove(bot.id)} data-testid={`bot-delete-${bot.symbol}`} title="Delete" className="p-2 hover:bg-zinc-800 rounded-sm text-zinc-400 hover:text-[#FF3B30]">
                    <Trash size={14} />
                  </button>
                  <Switch
                    checked={bot.enabled}
                    onCheckedChange={(c) => toggle(bot.id, c)}
                    data-testid={`bot-toggle-${bot.symbol}`}
                    className="data-[state=checked]:bg-[#00C805]"
                  />
                </div>
              </div>
            </Card>
          ))}
              </>
            );
          })()}
        </div>

        <Card>
          <CardHeader title="Recent signals" />
          <div className="divide-y divide-zinc-800 max-h-[560px] overflow-y-auto">
            {logs.map(l => (
              <div key={l.id} className="px-5 py-3">
                <div className="flex justify-between items-baseline">
                  <div className="mono text-sm font-semibold">{l.symbol}</div>
                  <div className={`mono text-[10px] uppercase tracking-widest ${l.signal === "buy" ? "profit" : l.signal === "sell" ? "loss" : "text-zinc-500"}`}>{l.signal}</div>
                </div>
                <div className="text-xs text-zinc-400 mt-1">{l.message}</div>
                <div className="mono text-[10px] text-zinc-600 mt-1">{new Date(l.at).toLocaleString()}</div>
              </div>
            ))}
            {logs.length === 0 && <div className="px-5 py-12 text-center mono text-xs text-zinc-600">No signals yet.</div>}
          </div>
        </Card>
      </div>

      <BacktestModal open={backtestOpen} onOpenChange={handleBacktestClose} config={backtestConfig} />
      <BotCompareModal open={compareOpen} onOpenChange={setCompareOpen} botIds={compareIds} />
    </div>
  );
}
